import { useEffect, useRef, useState } from 'react';
import { kanal, type Mitglied } from '@beholder/shared';
import type { BudgetStand, KursStand } from '@beholder/shared/module/budget';
import { api, modulPfad } from '../../core/api';
import { useLive } from '../../core/live';
import { useDaten } from '../../core/ui';

export const pfad = (projektId: string, rest = '') => modulPfad(projektId, 'budget', rest);

/**
 * Budgetstand laden und bei Änderungen anderer neu laden. Auf p:<projekt>:budget kommen
 * { typ: 'geaendert', was, id } vom Budget-Server und { typ: '__anlagen', bezug } vom Kern
 * (Anlage hochgeladen/gelöscht → Anlagen-Zahl am Beleg). Beides lädt den Stand neu (gebündelt).
 */
export function useBudget(projektId: string) {
  const d = useDaten(() => api.get<BudgetStand>(pfad(projektId)), [projektId]);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useLive(kanal(projektId, 'budget'), () => {
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => void d.neuLaden(), 150);
  });
  useEffect(() => () => {
    if (timer.current) clearTimeout(timer.current);
  }, []);
  return d;
}

export function useMitglieder(projektId: string) {
  return useDaten(() => api.get<Mitglied[]>(`/api/projekte/${projektId}/mitglieder`), [projektId]);
}

export interface MindmapKnoten {
  id: string;
  elternId: string | null;
  titel: string;
  pfad: string;
}

export function useMindmapKnoten(projektId: string) {
  return useDaten(() => api.get<{ verfuegbar: boolean; knoten: MindmapKnoten[] }>(pfad(projektId, '/mindmap-knoten')), [projektId]);
}

/** Kurse eines Tages (leer = aktuell). Wird je Tag nur einmal geladen. */
const kursSpeicher = new Map<string, Promise<KursStand>>();
export function useKurse(projektId: string, datum: string | null) {
  const [kurse, setKurse] = useState<KursStand | null>(null);
  const [fehler, setFehler] = useState('');
  useEffect(() => {
    let aktiv = true;
    const schl = `${projektId}|${datum ?? ''}`;
    let p = kursSpeicher.get(schl);
    if (!p) {
      p = api.get<KursStand>(datum ? pfad(projektId, `/kurse/tag?datum=${datum}`) : pfad(projektId, '/kurse'));
      kursSpeicher.set(schl, p);
      // Aktuelle Kurse nach 10 Minuten wieder frisch holen, Fehler nicht merken
      p.then(
        () => !datum && setTimeout(() => kursSpeicher.delete(schl), 10 * 60 * 1000),
        () => kursSpeicher.delete(schl),
      );
    }
    p.then(
      (k) => aktiv && (setKurse(k), setFehler('')),
      (f: unknown) => aktiv && setFehler(f instanceof Error ? f.message : 'Kurse nicht verfügbar'),
    );
    return () => {
      aktiv = false;
    };
  }, [projektId, datum]);
  return { kurse, fehler };
}

export const STATUS_KURZ: Record<string, string> = {
  offen: 'offen',
  bezahlt: 'bezahlt',
  umgewandelt: 'umgewandelt',
  abgelehnt: 'abgelehnt',
};
